import { z } from 'zod';
import { HomecareClientStatusSchema, HomecarePaymentModeSchema, } from './signup.schema.js';
import { RecipientResponseSchema } from './self.schema.js';
// ---------- list ----------
/** GET /api/v1/admin/homecare-clients — filters + offset pagination.
 *
 *  Sort: status ASC, createdAt DESC. Operator's main filter is by
 *  status (active accounts vs suspended needing review). */
export const AdminHomecareListQuerySchema = z.object({
    status: HomecareClientStatusSchema.optional(),
    paymentMode: HomecarePaymentModeSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminHomecareListItemSchema = z.object({
    id: z.string().uuid(),
    /** Account holder email (from the linked User row). */
    email: z.string().email(),
    displayName: z.string().nullable(),
    status: HomecareClientStatusSchema,
    paymentMode: HomecarePaymentModeSchema,
    /** True iff Stripe Customer object exists. */
    hasStripeCustomer: z.boolean(),
    recipientCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
});
export const AdminHomecareListResponseSchema = z.object({
    items: z.array(AdminHomecareListItemSchema),
    totalCount: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ---------- detail ----------
export const AdminHomecareDetailResponseSchema = z.object({
    id: z.string().uuid(),
    /** The account-holder User.id — keys the agreement-acceptance viewer
     *  (Phase 23), distinct from `id` which is the HomecareClient row id. */
    userId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    phone: z.string().nullable(),
    paymentMode: HomecarePaymentModeSchema,
    stripeCustomerId: z.string().nullable(),
    // 10d-4: legacy `defaultPaymentMethodId` scalar replaced by the
    // PaymentMethod table; the admin detail returns the boolean. Full
    // card-list lives at `/admin/homecare-clients/:id/payment-methods`.
    hasDefaultPaymentMethod: z.boolean(),
    languagePreference: z.string(),
    referralSource: z.string().nullable(),
    signupSubmittedIp: z.string().nullable(),
    signupSubmittedUserAgent: z.string().nullable(),
    status: HomecareClientStatusSchema,
    activatedAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    suspensionReason: z.string().nullable(),
    terminatedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /** All recipients including archived ones (operator surface; the
     *  account-holder GET /me only returns non-archived). */
    recipients: z.array(RecipientResponseSchema),
});
// ---------- lifecycle ----------
/** POST /api/v1/admin/homecare-clients/{id}/suspend — operator
 *  pauses an account for abuse, AR delinquency, etc. */
export const AdminHomecareSuspendRequestSchema = z.object({
    reason: z.string().min(1).max(500).trim(),
});
export const AdminHomecareUnsuspendRequestSchema = z.object({}).strict();
export const AdminHomecareTerminateRequestSchema = z.object({
    reason: z.string().min(1).max(500).trim(),
});
export const AdminHomecareStatusResponseSchema = z.object({
    id: z.string().uuid(),
    status: HomecareClientStatusSchema,
    activatedAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    suspensionReason: z.string().nullable(),
    terminatedAt: z.string().datetime().nullable(),
});
// ---------- retry external integrations ----------
/** POST /api/v1/admin/homecare-clients/{id}/retry-stripe — retries
 *  Stripe Customer creation if the original signup soft-degraded
 *  (e.g. STRIPE_SECRET_KEY was unset at signup time and is now
 *  configured). Returns the post-retry state. */
export const AdminHomecareRetryStripeResponseSchema = z.object({
    ok: z.boolean(),
    stripeCustomerId: z.string().nullable(),
    /** Brief human-readable reason when ok=false (e.g. "Stripe still
     *  not configured", "Stripe API rejected"). */
    message: z.string().nullable(),
});
//# sourceMappingURL=admin.schema.js.map